import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  ArrowClockwise,
  FilterCircle,
  PencilSquare,
  PlayBtn,
  SendCheck,
} from "react-bootstrap-icons";
import { Accordion } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";

// internal import
import Sidebar from "../../components/admin/sidebar/Sidebar";
import useDash from "../../assets/css/dash.module.css";
import { FontColor, FourGround } from "../../assets/js/theme";
import useISPowner from "../../hooks/useISPOwner";
import { getCustomer, getIPNMessage } from "../../features/apiCalls";
import Table from "../../components/table/Table";
import { badge } from "../../components/common/Utils";
import Loader from "../../components/common/Loader";
import IPNReferenceUpdate from "./modal/IPNReferenceUpdate";

const IPNMessage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // current date state
  var today = new Date();
  var firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  // get user & current user data form useISPOwner hook
  const { ispOwnerId, ispOwnerData, bpSettings, userData } = useISPowner();

  // get customers webhook paymnet message data
  const messages = useSelector((state) => state?.payment?.ipnMessage);

  //---> Get customer from redux store
  const customers = useSelector((state) => state.customer.customer);

  // Local State
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mainData, setMainData] = useState([]);
  const [modalStatus, setModalStatus] = useState("");
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [activeKeys, setActiveKeys] = useState("");
  const [status, setStatus] = useState("");
  const [filterDate, setFilterDate] = useState(firstDay);
  const [dateStart, setStartDate] = useState(firstDay);
  const [dateEnd, setEndDate] = useState(today);

  // filter date state
  var selectFirstDay = new Date(
    filterDate.getFullYear(),
    filterDate.getMonth(),
    1
  );

  var selectLastDay = new Date(
    filterDate.getFullYear(),
    filterDate.getMonth() + 1,
    0
  );

  selectFirstDay.setHours(0, 0, 0, 0);
  selectLastDay.setHours(23, 59, 59, 999);

  // get customer webhook paymnet message sending data from api
  const dataGet = {
    netfeeId: ispOwnerData.netFeeId,
    startDate: selectFirstDay,
    endDate: selectLastDay,
    limit: bpSettings?.customerLimit,
  };

  // api call
  useEffect(() => {
    setStartDate(selectFirstDay);

    if (selectLastDay.getMonth() + 1 === today.getMonth() + 1) {
      setEndDate(today);
    } else {
      setEndDate(selectLastDay);
    }

    filterDate.getMonth() + 1 && getIPNMessage(dispatch, dataGet, setIsLoading);

    //---> @Get ispOwner all customer data
    !customers?.length && getCustomer(dispatch, ispOwnerId, setLoading);
  }, [filterDate]);

  // set webhook message in state
  useEffect(() => {
    setMainData(messages);
  }, [messages]);

  // reload handler
  const reloadHandler = () => {
    getIPNMessage(dispatch, dataGet, setIsLoading);
  };

  const messageDataParse = (data) => {
    let messageObj = null;
    let innerMessage = null;

    try {
      messageObj = JSON.parse(data);
      innerMessage = JSON.parse(messageObj.Message);
    } catch (err) {
      console.error("JSON parse error:", err);
    }

    return innerMessage;
  };

  //
  const onClickFilter = () => {
    let arr = [...messages];

    if (status) {
      arr = arr.filter((item) => item.status === status);
    }

    arr = arr.filter(
      (item) =>
        new Date(item.createdAt) >= new Date(dateStart).setHours(0, 0, 0, 0) &&
        new Date(item.createdAt) <= new Date(dateEnd).setHours(23, 59, 59, 999)
    );

    setMainData(arr);
  };

  const columns = useMemo(
    () => [
      {
        width: "5%",
        Header: "#",
        id: "row",
        accessor: (row) => Number(row.id + 1),
        Cell: ({ row }) => <strong>{Number(row.id) + 1}</strong>,
      },
      {
        width: "10%",
        Header: t("mobile"),
        Cell: ({ row: { original } }) => (
          <p>{messageDataParse(original?.msg)?.debitMSISDN}</p>
        ),
      },
      {
        width: "10%",
        Header: t("amount"),
        Cell: ({ row: { original } }) => (
          <p>{Number(messageDataParse(original?.msg)?.amount)}</p>
        ),
      },
      {
        width: "10%",
        Header: t("reference"),
        accessor: "refId",
      },
      {
        width: "10%",
        Header: t("trxID"),
        Cell: ({ row: { original } }) => (
          <p>{messageDataParse(original?.msg)?.trxID}</p>
        ),
      },
      {
        width: "10%",
        Header: t("status"),
        accessor: "status",
        Cell: ({ row: { original } }) => {
          if (original.status === "REJECTED") {
            return (
              <span className="badge bg-danger">
                {original.rejectionValue + " " + original.status}
              </span>
            );
          }
          return badge(original.status);
        },
      },
      {
        width: "10%",
        Header: t("createdAt"),
        accessor: "createdAt",
        Cell: ({ cell: { value } }) => {
          return moment(value).format("YYYY-MM-DD hh:mm A");
        },
      },
      {
        width: "5%",
        Header: t("action"),
        id: "option",
        Cell: ({ row: { original } }) => {
          return (
            <div className="d-flex justify-content-center align-items-center">
              {original.status === "SENT" ? (
                <SendCheck size={20} color="green" />
              ) : (
                <PencilSquare
                  size={20}
                  color="red"
                  cursor="pointer"
                  title={t("edit")}
                  onClick={() => {
                    setModalStatus("reference");
                    setMessage(original);
                    setShow(true);
                  }}
                />
              )}
            </div>
          );
        },
      },
    ],
    [t]
  );

  return (
    <>
      <Sidebar />
      <ToastContainer position="top-right" theme="colored" />

      <div className={useDash.dashboardWrapper}>
        <div className="container-fluied collector">
          <div className="container">
            <FontColor>
              <FourGround>
                <div className="collectorTitle d-flex justify-content-between px-4">
                  <div>{t("ipnMessage")}</div>

                  <div
                    style={{ height: "45px" }}
                    className="d-flex align-items-center"
                  >
                    <div
                      onClick={() => {
                        if (!activeKeys) {
                          setActiveKeys("filter");
                        } else {
                          setActiveKeys("");
                        }
                      }}
                      title={t("filter")}
                    >
                      <FilterCircle className="addcutmButton" />
                    </div>

                    <div className="reloadBtn">
                      {isLoading ? (
                        <Loader />
                      ) : (
                        <ArrowClockwise
                          className="arrowClock"
                          onClick={() => reloadHandler()}
                          title={t("refresh")}
                        />
                      )}
                    </div>

                    <div className="addAndSettingIcon">
                      <PlayBtn
                        className="addcutmButton"
                        onClick={() => {
                          setModalStatus("playTutorial");
                          setShow(true);
                        }}
                        title={t("tutorial")}
                      />
                    </div>
                  </div>
                </div>
              </FourGround>

              <FourGround>
                <div className="mt-2">
                  <Accordion alwaysOpen activeKey={activeKeys}>
                    <Accordion.Item eventKey="filter">
                      <Accordion.Body>
                        <div className="displayGrid6">
                          <div>
                            <DatePicker
                              className="form-control mw-100 mt-0"
                              selected={filterDate}
                              onChange={(date) => setFilterDate(date)}
                              dateFormat="MMM-yyyy"
                              showMonthYearPicker
                              showFullMonthYearPicker
                              minDate={new Date(userData?.createdAt)}
                              maxDate={new Date()}
                            />
                          </div>
                          <div>
                            <DatePicker
                              className="form-control"
                              selected={dateStart}
                              onChange={(date) => setStartDate(date)}
                              dateFormat="MMM dd yyyy"
                              minDate={selectFirstDay}
                              maxDate={
                                selectLastDay.getMonth() + 1 ===
                                today.getMonth() + 1
                                  ? today
                                  : selectLastDay
                              }
                            />
                          </div>

                          <div>
                            <DatePicker
                              className="form-control"
                              selected={dateEnd}
                              onChange={(date) => setEndDate(date)}
                              dateFormat="MMM dd yyyy"
                              minDate={selectFirstDay}
                              maxDate={
                                selectLastDay.getMonth() + 1 ===
                                today.getMonth() + 1
                                  ? today
                                  : selectLastDay
                              }
                            />
                          </div>

                          <select
                            className="form-select mt-0"
                            onChange={(e) => setStatus(e.target.value)}
                          >
                            <option value="" defaultValue>
                              {t("status")}
                            </option>
                            <option value="SENT">{t("sent")}</option>
                            <option value="UNSENT">{t("unSent")}</option>
                            <option value="REJECTED">{t("rejected")}</option>
                            <option value="TRASH">{t("trash")}</option>
                          </select>

                          <div>
                            <button
                              className="btn btn-outline-primary w-140"
                              type="button"
                              onClick={onClickFilter}
                            >
                              {t("filter")}
                            </button>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>

                  <div className="collectorWrapper pb-2">
                    <div className="addCollector">
                      <div className="table-section">
                        <Table
                          isLoading={isLoading}
                          // customComponent={customComponent}
                          columns={columns}
                          data={mainData}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </FourGround>
            </FontColor>
          </div>
        </div>
      </div>

      {/* bKash ipn message reference id edit modal */}
      {modalStatus === "reference" && (
        <IPNReferenceUpdate show={show} setShow={setShow} message={message} />
      )}
    </>
  );
};
export default IPNMessage;
